import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { isTokenExpired } from '../api/client';

const AuthContext = createContext(null);

function getTokenPair() {
  let accessToken = localStorage.getItem('accessToken');
  let refreshToken = localStorage.getItem('refreshToken');
  // One-time migration: promote legacy snake_case keys and remove them.
  if (!accessToken) {
    const old = localStorage.getItem('access_token');
    if (old) { localStorage.setItem('accessToken', old); localStorage.removeItem('access_token'); accessToken = old; }
  }
  if (!refreshToken) {
    const old = localStorage.getItem('refresh_token');
    if (old) { localStorage.setItem('refreshToken', old); localStorage.removeItem('refresh_token'); refreshToken = old; }
  }
  return { accessToken, refreshToken };
}

function saveTokenPair(accessToken, refreshToken) {
  localStorage.setItem('accessToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
}

function clearTokenPair() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

async function tryRestoreSession(setUser) {
  const { accessToken, refreshToken } = getTokenPair();

  if (!accessToken && !refreshToken) return;

  // Access token valid — verify with server directly.
  if (accessToken && !isTokenExpired(accessToken)) {
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      return;
    } catch {
      // Server rejected it — fall through to refresh.
    }
  }

  // Access token missing or expired — try refresh proactively.
  if (refreshToken) {
    try {
      const res = await api.post('/api/auth/refresh', { refreshToken });
      localStorage.setItem('accessToken', res.data.accessToken);
      if (res.data.refreshToken) {
        localStorage.setItem('refreshToken', res.data.refreshToken);
      }
      const me = await api.get('/api/auth/me');
      setUser(me.data);
    } catch {
      clearTokenPair();
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tryRestoreSession(setUser).finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    saveTokenPair(res.data.accessToken, res.data.refreshToken);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (data) => {
    await api.post('/api/auth/register', data);
  };

  const logout = () => {
    clearTokenPair();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
